/* eslint-disable eqeqeq */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Dropdown, Table, Button, Modal } from "react-bootstrap";
import { useCallback, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "../hooks/LanguageProvider";
import { PropagateLoader } from "react-spinners";
import {
  DownloadModal,
  generateExcelFile,
  generatePDFFile,
  addHiddenColumn,
  removeHiddenColumn,
  getHiddenColumns,
} from "../utilities/functions";

import EditOEMModal from "../components/OEM/EditOEMModal";
import DeleteOEMModal from "../components/OEM/DeleteOEMModal";
import {
  AIDA_COMPANY_TYPE_OPTIONS,
  getAidaOptionLabel,
} from "../constants/aidaClassification";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:9000";
const DEFAULT_OEM_LOGO = "/asset/images/default-oem.png";
const apiOrigin = backendUrl.replace(/\/api\/?$/i, "").replace(/\/$/, "");

const getOEMLogoUrl = (logo?: string | null) => {
  const value = String(logo || "").trim();

  if (!value) return DEFAULT_OEM_LOGO;
  if (/^https?:\/\//i.test(value)) return value;

  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  if (normalizedPath.startsWith("/uploads/")) {
    return `${apiOrigin}/api${normalizedPath}`;
  }

  return `${apiOrigin}${normalizedPath}`;
};

interface OEMInterface {
  id?: number;
  id_oem?: number;
  id_user?: number;
  user_id?: number;
  logo?: string | null;
  company_name?: string;
  industry_sector?: string;
  company_type?: string;
  country?: string;
  city?: string;
  headquarters?: string;
  address?: string;
  email?: string;
  contact_email?: string;
  phone?: string;
  website?: string;
  status?: string;
  industrial_status?: string;
}

export function OEMs() {
  const PAGE_ID = 7;
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const id_user = user?.id_user ?? 0;
  const id_user_number = Number(user?.id_user ?? 0);

  const { translate } = useTranslate();

  const [list_oems, setOEMs] = useState<OEMInterface[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [limitValue, setSelectedLimitValue] = useState(10);
  const [loading, setLoading] = useState(true);

  const [showCreateUserFirstModal, setShowCreateUserFirstModal] =
    useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const [modalStatus, setModalStatus] = useState<string | null>(null);
  const [titleStatus, setTitleStatus] = useState<string | null>(null);
  const [IdUser, setIdUser] = useState<number>(0);
  const [IdOEM, setIdOEM] = useState<number>(0);
  const [selectedOEM, setSelectedOEM] = useState<OEMInterface | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [column, setSortColumn] = useState("company_name");
  const [sort, setSort] = useState("ASC");

  const [search, setSearch] = useState("");
  const [type, setType] = useState(0);
  const [typeSearch, setTypeSearch] = useState("company_name");
  const [selectedSearch, setSelectedSearch] = useState(
    translate("company_name"),
  );

  const [selectedColumns, setSelectedColumns] = useState({
    id_oem: true,
    company_name: true,
    industry_sector: true,
    company_type: true,
    email: true,
    phone: true,
    country: true,
    city: true,
    status: true,
  });

  const [selectedOEMs, setSelectedOEMs] = useState<Set<number>>(new Set());

  const getOEMId = (oem: OEMInterface) => Number(oem.id_oem ?? oem.id ?? 0);

  const getOEMUserId = (oem: OEMInterface) =>
    Number(oem.id_user ?? oem.user_id ?? 0);

  const getOEMEmail = (oem: OEMInterface) =>
    oem.email || oem.contact_email || "-";

  const getOEMStatus = (oem: OEMInterface) =>
    oem.industrial_status || oem.status || "incomplete";

  const getStatusBadge = (status: string) => {
    const value = (status || "incomplete").toLowerCase();

    const config = {
      active: {
        label: translate("active"),
        bg: "linear-gradient(135deg, #ecfdf3 0%, #d1fae5 100%)",
        color: "#166534",
        border: "#86efac",
        dot: "#22c55e",
        shadow: "0 4px 14px rgba(34, 197, 94, 0.16)",
      },
      inactive: {
        label: translate("inactive"),
        bg: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
        color: "#b91c1c",
        border: "#fda4af",
        dot: "#ef4444",
        shadow: "0 4px 14px rgba(239, 68, 68, 0.14)",
      },
      incomplete: {
        label: translate("incomplete"),
        bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
        color: "#b45309",
        border: "#fcd34d",
        dot: "#f59e0b",
        shadow: "0 4px 14px rgba(245, 158, 11, 0.14)",
      },
    };

    const current = config[value as keyof typeof config] || config.incomplete;

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 14px",
          borderRadius: "999px",
          background: current.bg,
          color: current.color,
          border: `1px solid ${current.border}`,
          boxShadow: current.shadow,
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: current.dot,
          }}
        />
        {current.label}
      </span>
    );
  };

  const actionButtonStyle = {
    width: "38px",
    height: "38px",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  } as const;

  useEffect(() => {
    (async () => {
      if (!id_user_number) return;
      try {
        const hidden = await getHiddenColumns(id_user_number, PAGE_ID);
        setSelectedColumns((prev: any) => {
          const next = { ...prev };
          hidden.forEach((key: string) => {
            if (key in next) next[key] = false;
          });
          return next;
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, [id_user_number]);

  const oemHeaders = [
    "ID",
    "Company name",
    "Industry",
    "Type d'entreprise",
    "Email",
    "Phone",
    "Country",
    "City",
    "Status",
  ];

  const downloadOEMExcel = () => {
    const selectedData = list_oems.map((oem) => [
      getOEMId(oem),
      oem.company_name || "-",
      oem.industry_sector || "-",
      getAidaOptionLabel(AIDA_COMPANY_TYPE_OPTIONS, oem.company_type),
      getOEMEmail(oem),
      oem.phone || "-",
      oem.country || "-",
      oem.city || "-",
      getOEMStatus(oem),
    ]);

    generateExcelFile("OEMs List", oemHeaders, selectedData);
  };

  const onDownloadConfirm = (format: string) => {
    const rows = list_oems.map((oem) => [
      getOEMId(oem),
      oem.company_name || "-",
      oem.industry_sector || "-",
      getAidaOptionLabel(AIDA_COMPANY_TYPE_OPTIONS, oem.company_type),
      getOEMEmail(oem),
      oem.phone || "-",
      oem.country || "-",
      oem.city || "-",
      getOEMStatus(oem),
    ]);

    if (format === "excel") {
      generateExcelFile("OEMs List", oemHeaders, rows);
    }

    if (format === "pdf") {
      generatePDFFile("OEMs List", oemHeaders, rows);
    }
  };

  const handleGoToAddOEMUser = () => {
    setShowCreateUserFirstModal(false);
    navigate("/User?role=oem");
  };

  const handleCloseCreateUserFirstModal = () => {
    setShowCreateUserFirstModal(false);
  };

  const handleTypeSearch = (event: any) => {
    const selectedValue = event.target.textContent;

    switch (selectedValue) {
      case translate("id"):
        setType(0);
        setTypeSearch("id");
        break;
      case translate("company_name"):
        setType(1);
        setTypeSearch("company_name");
        break;
      case translate("industry"):
        setType(2);
        setTypeSearch("industry");
        break;
      case translate("email"):
        setType(3);
        setTypeSearch("email");
        break;
      case translate("phone"):
        setType(4);
        setTypeSearch("phone");
        break;
      case translate("country"):
        setType(5);
        setTypeSearch("country");
        break;
      case translate("city"):
        setType(6);
        setTypeSearch("city");
        break;
      case translate("status"):
        setType(7);
        setTypeSearch("status");
        break;
      default:
        setType(1);
        setTypeSearch("company_name");
    }

    setSelectedSearch(selectedValue);
    setCurrentPage(1);
  };

  const Options = [
    "id",
    "company_name",
    "industry",
    "email",
    "phone",
    "country",
    "city",
    "status",
  ];

  const handleAdvancedSearch = async (event: any) => {
    const newValue = event.target.value;
    setSearch(newValue);
    setCurrentPage(1);
    await getOEMs(limitValue, 1, newValue, type, column, sort);
  };

  const handleColumnChange = async (colKey: string) => {
    if (!id_user_number) return;

    const currentlyVisible = !!(selectedColumns as any)[colKey];
    const nextVisible = !currentlyVisible;

    setSelectedColumns((prev: any) => ({ ...prev, [colKey]: nextVisible }));

    try {
      if (!nextVisible) {
        await addHiddenColumn({
          id_user: id_user_number,
          id_page: PAGE_ID,
          column_name: colKey,
        });
      } else {
        await removeHiddenColumn({
          id_user: id_user_number,
          id_page: PAGE_ID,
          column_name: colKey,
        });
      }
    } catch (e) {
      console.error(e);
      setSelectedColumns((prev: any) => ({
        ...prev,
        [colKey]: currentlyVisible,
      }));
    }
  };

  const handleSortingColum = (currentColumn: string) => {
    const nextSort = sort === "DESC" ? "ASC" : "DESC";
    setSort(nextSort);
    setSortColumn(currentColumn);
    setCurrentPage(1);
    getOEMs(limitValue, 1, search, type, currentColumn, nextSort);
  };

  const handleDeleteOEM = async (id_oem: string, currentUserId: number) => {
    try {
      setModalStatus(translate("delete_oem_confirm"));
      setTitleStatus(translate("delete_oem"));
      setIdUser(currentUserId);
      setIdOEM(parseInt(id_oem));
    } catch (error) {
      console.error(error);
    }
  };

  const closeModal = () => {
    setModalStatus(null);
  };

  const handleSelectChange = async (event: any) => {
    const newValue = Number(event.target.value);
    setCurrentPage(1);
    setSelectedLimitValue(newValue);

    const oemsFromServer = await getOEMs(
      newValue,
      1,
      search,
      type,
      column,
      sort,
    );
    setOEMs(oemsFromServer || []);
    window.scrollTo(0, 0);
  };

  const getOEMs = useCallback(
    async (
      limitValue: number,
      currentPage: number,
      search: string,
      type: number,
      column: string,
      sort: string,
    ) => {
      const bodyData = JSON.stringify({
        limitValue,
        currentPage,
        search,
        type,
        id_user,
        column,
        sort,
      });

      try {
        setLoading(true);

        const total_pages = await fetch(
          `${backendUrl}/api/oems/search/totalpage`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: bodyData,
          },
        );

        const totalPages = await total_pages.json();
        const t = Number(totalPages?.total || 0);
        setTotal(t);

        const oemResponse = await fetch(`${backendUrl}/api/oems/search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: bodyData,
          mode: "cors",
        });

        if (!oemResponse.ok) {
          console.error("Error retrieving OEMs list");
          setLoading(false);
          return [];
        }

        const data = await oemResponse.json();
        setPageCount(Math.ceil(t / limitValue));
        setOEMs(Array.isArray(data) ? data : []);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error(error);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [id_user],
  );

  useEffect(() => {
    getOEMs(limitValue, currentPage, search, type, column, sort);
  }, [column, currentPage, getOEMs, limitValue, search, sort, type]);

  const updateOEMList = async () => {
    await getOEMs(limitValue, currentPage, search, type, column, sort);
  };

  const handlePageClick = async (data: any) => {
    const nextPage = data.selected + 1;
    await getOEMs(limitValue, nextPage, search, type, column, sort);
    setCurrentPage(nextPage);
    window.scrollTo(0, 0);
  };

  const handleSelectAll = (e: any) => {
    e.stopPropagation();

    if (e.target.checked) {
      const allIds = new Set(
        list_oems.map((oem) => getOEMId(oem)).filter(Boolean),
      );
      setSelectedOEMs(allIds);
    } else {
      setSelectedOEMs(new Set());
    }
  };

  const handleSelectOEM = (e: any, id_oem: number) => {
    e.stopPropagation();
    const newSelected = new Set(selectedOEMs);

    if (newSelected.has(id_oem)) {
      newSelected.delete(id_oem);
    } else {
      newSelected.add(id_oem);
    }

    setSelectedOEMs(newSelected);
  };

  const isAllSelected =
    list_oems.length > 0 &&
    selectedOEMs.size ===
    list_oems.map((oem) => getOEMId(oem)).filter(Boolean).length;

  const isSomeSelected =
    selectedOEMs.size > 0 && selectedOEMs.size < list_oems.length;

  useEffect(() => {
    setSelectedOEMs(new Set());
  }, [currentPage, list_oems]);

  useEffect(() => {
    if (modalStatus === "success") {
      const timeoutId = setTimeout(() => {
        setModalStatus(null);
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [modalStatus]);

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-md-6">
          <h4 className="mb-0">
            <i className="las la-industry me-2"></i>
            {translate("oems")} ({total})
          </h4>
        </div>

        <div className="col-md-6 text-end">
          <Dropdown className="d-inline me-2">
            <Dropdown.Toggle variant="warning">
              {translate("export")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  onDownloadConfirm("excel");
                  setShowDownloadModal(false);
                }}
              >
                Excel
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  onDownloadConfirm("pdf");
                  setShowDownloadModal(false);
                }}
              >
                PDF
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <button
            className="btn btn-warning"
            onClick={() => setShowCreateUserFirstModal(true)}
          >
            + {translate("new_oem")}
          </button>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-4">
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link">
                <i className="fas fa-chevron-down"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu onClick={handleTypeSearch}>
                {Options.map((option) => (
                  <Dropdown.Item key={option}>
                    {translate(option)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              className="form-control"
              placeholder={`${translate("search_by")} ${translate(typeSearch)}`}
              value={search}
              onChange={handleAdvancedSearch}
            />

            <Button
              variant="secondary"
              style={{ height: "38px" }}
              title={translate("clear_search")}
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
                getOEMs(limitValue, 1, "", type, column, sort);
              }}
            >
              ×
            </Button>
          </div>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length" id="DataTables_Table_0_length">
            <label style={{ marginBottom: "0" }}>
              {translate("Show")}
              <select
                name="DataTables_Table_0_length"
                aria-controls="DataTables_Table_0"
                className="custom-select custom-select-sm form-control form-control-sm ml-2"
                style={{ width: "66px" }}
                value={limitValue}
                onChange={handleSelectChange}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>

          <Dropdown>
            <Dropdown.Toggle
              variant=""
              id="dropdown-basic"
              title={translate("Display columns")}
            >
              <i className="las la-eye"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item as="button">
                <input
                  type="checkbox"
                  checked={selectedColumns.id_oem}
                  onChange={() => handleColumnChange("id_oem")}
                />
                <span>{translate("id")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button">
                <input
                  type="checkbox"
                  checked={selectedColumns.company_name}
                  onChange={() => handleColumnChange("company_name")}
                />
                <span>{translate("company_name")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button">
                <input
                  type="checkbox"
                  checked={selectedColumns.industry_sector}
                  onChange={() => handleColumnChange("industry_sector")}
                />
                <span>{translate("industry")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button">
                <input
                  type="checkbox"
                  checked={selectedColumns.company_type}
                  onChange={() => handleColumnChange("company_type")}
                />
                <span>Type d'entreprise</span>
              </Dropdown.Item>

              <Dropdown.Item as="button">
                <input
                  type="checkbox"
                  checked={selectedColumns.email}
                  onChange={() => handleColumnChange("email")}
                />
                <span>{translate("email")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button">
                <input
                  type="checkbox"
                  checked={selectedColumns.phone}
                  onChange={() => handleColumnChange("phone")}
                />
                <span>{translate("phone")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button">
                <input
                  type="checkbox"
                  checked={selectedColumns.country}
                  onChange={() => handleColumnChange("country")}
                />
                <span>{translate("country")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button">
                <input
                  type="checkbox"
                  checked={selectedColumns.city}
                  onChange={() => handleColumnChange("city")}
                />
                <span>{translate("city")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button">
                <input
                  type="checkbox"
                  checked={selectedColumns.status}
                  onChange={() => handleColumnChange("status")}
                />
                <span>{translate("status")}</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div className="row">
      <div className="table-responsive">
        <Table className="dataTable table">
          <thead className="bg-white text-uppercase">
            <tr className="ligth ligth-data">
              <th style={{ width: "5%" }}>
                <div className="checkbox d-inline-block">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isSomeSelected;
                    }}
                    onChange={handleSelectAll}
                  />
                </div>
              </th>

              {selectedColumns.id_oem && (
                <th className="sorting" onClick={() => handleSortingColum("id")}>
                  {translate("id")}
                </th>
              )}

              {selectedColumns.company_name && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("company_name")}
                >
                  {translate("company_name")}
                </th>
              )}

              {selectedColumns.industry_sector && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("industry_sector")}
                >
                  {translate("industry")}
                </th>
              )}

              {selectedColumns.company_type && <th>Type d'entreprise</th>}

              {selectedColumns.email && <th>{translate("email")}</th>}
              {selectedColumns.phone && <th>{translate("phone")}</th>}

              {selectedColumns.country && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("country")}
                >
                  {translate("country")}
                </th>
              )}

              {selectedColumns.city && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("city")}
                >
                  {translate("city")}
                </th>
              )}

              {selectedColumns.status && <th>{translate("status")}</th>}

              <th>{translate("actions")}</th>
            </tr>
          </thead>

          <tbody className="ligth-body">
            {loading ? (
              <tr>
                <td className="text-center" colSpan={10}>
                  <PropagateLoader size={15} />
                </td>
              </tr>
            ) : list_oems.length > 0 ? (
              list_oems.map((oem) => {
                const rowOEMId = getOEMId(oem);
                const rowUserId = getOEMUserId(oem);
                const companyName = oem.company_name || "-";
                const oemLogo = getOEMLogoUrl(oem.logo);

                return (
                  <tr key={rowOEMId || Math.random()}>
                    <td>
                      <div className="checkbox d-inline-block">
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={selectedOEMs.has(rowOEMId)}
                          onChange={(e) => handleSelectOEM(e, rowOEMId)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </td>

                    {selectedColumns.id_oem && <td>{rowOEMId || "-"}</td>}
                    {selectedColumns.company_name && (
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <img
                            src={oemLogo}
                            alt={companyName}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = DEFAULT_OEM_LOGO;
                            }}
                            style={{
                              width: 42,
                              height: 42,
                              borderRadius: 8,
                              objectFit: "contain",
                              backgroundColor: "#f8fafc",
                              border: "1px solid #e5e7eb",
                              padding: 4,
                              flexShrink: 0,
                            }}
                          />
                          <div>
                            <div className="fw-semibold">{companyName}</div>
                            {oem.headquarters && (
                              <div className="small text-muted">
                                {oem.headquarters}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    {selectedColumns.industry_sector && (
                      <td>{oem.industry_sector || "-"}</td>
                    )}
                    {selectedColumns.company_type && (
                      <td>
                        {getAidaOptionLabel(
                          AIDA_COMPANY_TYPE_OPTIONS,
                          oem.company_type,
                        )}
                      </td>
                    )}
                    {selectedColumns.email && <td>{getOEMEmail(oem)}</td>}
                    {selectedColumns.phone && <td>{oem.phone || "-"}</td>}
                    {selectedColumns.country && <td>{oem.country || "-"}</td>}
                    {selectedColumns.city && <td>{oem.city || "-"}</td>}
                    {selectedColumns.status && (
                      <td>{getStatusBadge(getOEMStatus(oem))}</td>
                    )}

                    <td className="text-center">
                      <button
                        className="btn btn-sm me-2 rounded-circle"
                        style={{
                          ...actionButtonStyle,
                          backgroundColor: "#020815",
                          border: "1px solid #020815",
                          color: "#ffffff",
                        }}
                        title={translate("go_to_profile")}
                        onClick={() => {
                          if (!rowUserId) {
                            console.error("No OEM user id found:", oem);
                            return;
                          }

                          navigate(`/oem-profile/${rowUserId}`, {
                            state: { canEditCompany: true },
                          });
                        }}
                      >
                        <i className="fas fa-user"></i>
                      </button>

                      <button
                        className="btn btn-sm btn-warning me-2 rounded-circle"
                        style={actionButtonStyle}
                        title={translate("edit")}
                        onClick={() => {
                          setSelectedOEM(oem);
                          setShowEditModal(true);
                        }}
                      >
                        <i className="fas fa-edit"></i>
                      </button>

                      <button
                        className="btn btn-sm btn-danger rounded-circle"
                        style={actionButtonStyle}
                        title={translate("delete")}
                        onClick={() =>
                          handleDeleteOEM(String(rowOEMId), Number(id_user || 0))
                        }
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={10}>{translate("no_oems")}</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      </div>

      <div className="d-flex justify-content-between mt-3">
        <span>
          {translate("displaying")} {list_oems.length} {translate("out_of")}{" "}
          {total}
        </span>

        <div>
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-end"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
            forcePage={currentPage - 1}
          />
        </div>
      </div>
      <Modal
        show={showCreateUserFirstModal}
        onHide={handleCloseCreateUserFirstModal}
        centered
        backdrop="static"
      >
        <div
          style={{
            background: "#ffffff",
            border: "none",
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
          }}
        >
          <Modal.Header
            closeButton
            style={{
              borderBottom: "1px solid #eef2f7",
              padding: "28px 32px 20px 32px",
              background: "#ffffff",
            }}
          >
            <div className="d-flex align-items-start gap-3 w-100">
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "14px",
                  background:
                    "linear-gradient(135deg, #fff7d6 0%, #ffe58f 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 8px 20px rgba(245, 158, 11, 0.18)",
                }}
              >
                <i
                  className="las la-user-plus"
                  style={{
                    fontSize: "26px",
                    color: "#a16207",
                  }}
                />
              </div>

              <div>
                <Modal.Title
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#0f172a",
                    lineHeight: 1.2,
                    letterSpacing: "-0.4px",
                  }}
                >
                  {translate("new_oem_title")}
                </Modal.Title>

                <div
                  style={{
                    marginTop: "10px",
                    fontSize: "16px",
                    color: "#64748b",
                    fontWeight: 400,
                  }}
                >
                  {translate("create_oem_description")}
                </div>
              </div>
            </div>
          </Modal.Header>

          <Modal.Footer
            style={{
              borderTop: "1px solid #eef2f7",
              padding: "20px 32px 24px 32px",
              background: "#ffffff",
              justifyContent: "center",
              gap: "12px",
            }}
          >
            <Button
              variant="light"
              onClick={handleCloseCreateUserFirstModal}
              style={{
                minWidth: "140px",
                height: "48px",
                borderRadius: "10px",
                border: "1px solid #d0d7e2",
                color: "#475569",
                fontWeight: 600,
                background: "#ffffff",
              }}
            >
              {translate("cancel")}
            </Button>

            <Button
              onClick={handleGoToAddOEMUser}
              style={{
                minWidth: "180px",
                height: "48px",
                borderRadius: "10px",
                border: "none",
                fontWeight: 700,
                background: "linear-gradient(135deg, #facc15 0%, #f59e0b 100%)",
                color: "#111827",
                boxShadow: "0 10px 24px rgba(245, 158, 11, 0.22)",
              }}
            >
              <i className="las la-plus-circle me-2" />
              {translate("add_new_user")}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
      <EditOEMModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedOEM(null);
        }}
        oem={selectedOEM}
        onSuccess={updateOEMList}
      />

      <DeleteOEMModal
        show={modalStatus !== null}
        onHide={closeModal}
        status={modalStatus}
        title={titleStatus}
        IdUser={IdUser}
        IdOEM={IdOEM}
        updateOEMList={updateOEMList}
      />

      <DownloadModal
        show={showDownloadModal}
        onHide={() => setShowDownloadModal(false)}
        onDownloadConfirm={onDownloadConfirm}
      />
    </div>
  );
}

